/**
 * Constants for Matches feature
 *
 * Configurable labels for event types, competitions, and sports.
 * These can be extended or overridden via API in the future.
 */

// Event types (not just football matches)
export const EVENT_TYPES = {
  match: { label: 'Match', color: '#68ff9a' },
  training: { label: 'Training', color: '#8ce5ff' },
  press_conference: { label: 'Press Conference', color: '#d4a5ff' },
  photoshoot: { label: 'Photoshoot', color: '#ffb366' },
  other: { label: 'Other Event', color: '#a0a0a0' },
};

// Sports types
export const SPORTS = {
  football: { label: 'Football', icon: null },
  tennis: { label: 'Tennis', icon: null },
  basketball: { label: 'Basketball', icon: null },
  hockey: { label: 'Hockey', icon: null },
  volleyball: { label: 'Volleyball', icon: null },
  athletics: { label: 'Athletics', icon: null },
  other: { label: 'Other', icon: null },
};

// Competition/League labels (football focused but extensible)
export const COMPETITIONS = {
  // Dutch
  eredivisie: { label: 'Eredivisie', shortCode: 'ERE', color: '#ff6b6b', country: 'NL' },
  eerste_divisie: { label: 'Eerste Divisie', shortCode: 'EED', color: '#ffb366', country: 'NL' },
  tweede_divisie: { label: 'Tweede Divisie', shortCode: 'TWD', color: '#ffb366', country: 'NL' },
  keuken_kampioen: { label: 'Keuken Kampioen Divisie', shortCode: 'KKD', color: '#ffb366', country: 'NL' },
  knvb_beker: { label: 'KNVB Beker', shortCode: 'KNV', color: '#66b7ff', country: 'NL' },
  eredivisie_vrouwen: { label: 'Eredivisie Vrouwen', shortCode: 'EVR', color: '#e066ff', country: 'NL' },
  nationale_jeugd_competitie: { label: 'Nationale Jeugd Competitie', shortCode: 'NJC', color: '#3cd4ce', country: 'NL' },
  // European
  champions_league: { label: 'Champions League', shortCode: 'UCL', color: '#1a56db', country: 'EU' },
  europa_league: { label: 'Europa League', shortCode: 'UEL', color: '#ff9b64', country: 'EU' },
  conference_league: { label: 'Conference League', shortCode: 'ECL', color: '#68ff9a', country: 'EU' },
  // International
  world_cup: { label: 'World Cup', shortCode: 'WCF', color: '#ffd700', country: 'INT' },
  euros: { label: 'European Championship', shortCode: 'EUR', color: '#4169e1', country: 'EU' },
  nations_league: { label: 'Nations League', shortCode: 'UNL', color: '#9b59b6', country: 'EU' },
  // Friendlies (split P7 — `friendly` kept for back-compat so existing
  // matches keep resolving; new matches should pick one of the two
  // disambiguating slugs below).
  club_friendly: { label: 'Club Friendly', shortCode: 'FRC', color: '#888888', country: 'NL' },
  international_friendly: { label: 'International Friendly', shortCode: 'INF', color: '#888888', country: 'INT' },
  friendly: { label: 'Friendly', shortCode: 'FRI', color: '#888888', country: null },
  // Other
  other: { label: 'Other', shortCode: 'OTH', color: '#a0a0a0', country: null },
};

// Match status colors
export const MATCH_STATUS_COLORS = {
  upcoming: '#66b7ff',
  live: '#ff6b6b',
  past: '#888888',
  cancelled: '#ff6b6b',
};

// Assignment status
export const ASSIGNMENT_STATUS = {
  unassigned: { label: 'Unassigned', color: '#ff9b64' },
  partially_assigned: { label: 'Partial', color: '#ffeb7a' },
  fully_assigned: { label: 'Assigned', color: '#68ff9a' },
};

// Filter options for match list
export const MATCH_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'live', label: 'Live' },
  { key: 'past', label: 'Past' },
  { key: 'today', label: 'Today' },
  { key: 'no_photographer', label: 'No Photographer' },
  { key: 'no_editor', label: 'No Editor' },
];

// Day names for calendar
export const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const WEEKDAYS_SHORT = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

// Month names
export const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// Get color for event based on type and competition
export function getEventColor(event) {
  if (event.competition && COMPETITIONS[event.competition]) {
    return COMPETITIONS[event.competition].color;
  }
  if (event.event_type && EVENT_TYPES[event.event_type]) {
    return EVENT_TYPES[event.event_type].color;
  }
  return EVENT_TYPES.match.color;
}

// Get label for competition
export function getCompetitionLabel(competitionKey) {
  return COMPETITIONS[competitionKey]?.label || competitionKey || 'Unknown';
}

// Get label for event type
export function getEventTypeLabel(typeKey) {
  return EVENT_TYPES[typeKey]?.label || typeKey || 'Event';
}

/**
 * Derive a football-season label like "2025/2026" from a Date.
 *
 * Convention: matches in Jan–Jul (month index 0–6) belong to the season
 * that started the previous calendar year; Aug–Dec (7–11) start a new
 * season. Mirrors the server-side derive_season() helper so Tank's
 * preview matches the final export.
 */
export function computeSeason(date) {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const year = d.getFullYear();
  const startYear = d.getMonth() < 7 ? year - 1 : year;
  return `${startYear}/${startYear + 1}`;
}

/**
 * Generate season dropdown options. Returns an array of `{ value, label }`
 * covering current year ± 2, plus the "" (auto) sentinel that tells the
 * server to derive from start_time.
 */
export function seasonOptions(now = new Date()) {
  const current = computeSeason(now);
  const year = now.getFullYear();
  const baseYear = now.getMonth() < 7 ? year - 1 : year;
  const seasons = [];
  for (let offset = -2; offset <= 2; offset += 1) {
    const start = baseYear + offset;
    seasons.push(`${start}/${start + 1}`);
  }
  return [
    { value: '', label: `Auto (${current})` },
    ...seasons.map((s) => ({ value: s, label: s })),
  ];
}
